import { zodResolver } from "@hookform/resolvers/zod";
import { Upload } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAllSchemas, useStartUploadJob, type StartUploadJobParams } from "@noa/api-client";
import type { SharePointSyncForm } from "~/utils/sharepoint-sync-form";
import { UploadJobProgress } from "./upload-job-progress";
import { UploadDropzoneCard } from "./upload-dropzone-card";
import { UploadConfigForm } from "./upload-config-form";
import { UploadOptionsCard } from "./upload-options-card";
import {
  preserveFilePaths,
  uploadFormDefaultValues,
  uploadFormSchema,
  type UploadForm,
  type UploadMode,
} from "~/utils/upload-form";
import { Alert, ConfirmDialog, Text, Title } from "~/components/ui";

type UploadPanelProps = {
  activeJobId: string | null;
  onActiveJobIdChange: (jobId: string | null) => void;
  onRelaunchSync?: (values: SharePointSyncForm, fromJobId: string) => void;
};

export function UploadPanel({ activeJobId, onActiveJobIdChange, onRelaunchSync }: UploadPanelProps) {
  const { t } = useTranslation("admin");
  const [fileList, setFileList] = useState<File[]>([]);
  const [uploadMode, setUploadMode] = useState<UploadMode>("files");
  // Values awaiting the delete-collection confirmation; non-null opens the dialog
  const [pendingDeleteValues, setPendingDeleteValues] = useState<UploadForm | null>(null);

  const { control, handleSubmit, watch, reset } = useForm<UploadForm>({
    resolver: zodResolver(uploadFormSchema),
    defaultValues: uploadFormDefaultValues,
  });
  const withVision = watch("withVision");

  const { schemas, isLoading: schemasLoading } = useAllSchemas();
  const startUpload = useStartUploadJob();

  const handleUploadModeChange = (mode: UploadMode) => {
    setUploadMode(mode);
    setFileList([]); // Clear files when switching modes
  };

  const performUpload = async (values: UploadForm) => {
    // Preserve folder structure by renaming files with their relative paths
    const files = preserveFilePaths(fileList);

    const result = await startUpload.mutateAsync({
      files,
      prefix: values.prefix,
      schemaId: values.schemaId,
      parser: values.parser as StartUploadJobParams["parser"],
      withVision: values.withVision,
      overwrite: values.overwrite,
      deleteCollection: values.deleteCollection,
      skipIngestion: values.skipIngestion,
    });

    onActiveJobIdChange(result.job_id);
    setFileList([]);
    reset(uploadFormDefaultValues);
  };

  const handleFormSubmit = useCallback(
    (values: UploadForm) => {
      if (fileList.length === 0) {
        return;
      }

      if (values.deleteCollection) {
        setPendingDeleteValues(values);
      } else {
        void performUpload(values);
      }
    },
    [fileList, performUpload]
  );

  const handleConfirmDeleteUpload = async () => {
    if (!pendingDeleteValues) return;
    try {
      await performUpload(pendingDeleteValues);
      setPendingDeleteValues(null);
    } catch {
      // Stay open so the admin can retry, as Modal.confirm did on a rejected onOk
    }
  };

  // Keyboard shortcut: Ctrl+Enter to submit
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && fileList.length > 0 && !startUpload.isPending) {
        e.preventDefault();
        void handleSubmit(handleFormSubmit)();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [fileList.length, startUpload.isPending, handleSubmit, handleFormSubmit]);

  const handleNewUpload = () => {
    onActiveJobIdChange(null);
  };

  // Show job progress view
  if (activeJobId) {
    return (
      <div className="flex flex-col gap-8">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Upload className="size-8 text-blue-500" />
          <div>
            <Title level={4} className="!mb-0">
              {t("uploadAdmin.progress.title")}
            </Title>
            <Text tone="muted">{t("uploadAdmin.progress.subtitle")}</Text>
          </div>
        </div>

        <UploadJobProgress jobId={activeJobId} onNewUpload={handleNewUpload} onRelaunchSync={onRelaunchSync} />
      </div>
    );
  }

  const schemaOptions = schemas.map((s) => ({
    value: s.schema_id,
    label: `${s.schema_id} — ${s.description || s.doc_type}`,
  }));

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Upload className="size-8 text-blue-500" />
        <div>
          <Title level={4} className="!mb-0">
            {t("uploadAdmin.upload.title")}
          </Title>
          <Text tone="muted">{t("uploadAdmin.upload.description")}</Text>
        </div>
      </div>

      <UploadDropzoneCard
        control={control}
        withVision={withVision}
        fileList={fileList}
        uploadMode={uploadMode}
        onUploadModeChange={handleUploadModeChange}
        onFileListChange={setFileList}
        isSubmitting={startUpload.isPending}
        onSubmit={handleSubmit(handleFormSubmit)}
      />

      <UploadConfigForm control={control} schemaOptions={schemaOptions} schemasLoading={schemasLoading} />

      <UploadOptionsCard control={control} />

      {/* Success message */}
      {startUpload.isSuccess && (
        <Alert
          type="success"
          message={t("uploadAdmin.messages.started")}
          description={
            <div className="inline-flex flex-col gap-2">
              <Text>
                {t("uploadAdmin.sync.taskId")}:{" "}
                <code className="bg-noa-hover px-2 py-1 rounded">{startUpload.data?.job_id}</code>
              </Text>
            </div>
          }
          showIcon
          closable
        />
      )}

      <ConfirmDialog
        open={pendingDeleteValues !== null}
        title={t("uploadAdmin.form.deleteCollectionWarning")}
        content={t("uploadAdmin.form.deleteCollectionConfirm")}
        danger
        okText={t("common:confirm")}
        cancelText={t("common:cancel")}
        confirmLoading={startUpload.isPending}
        onOk={() => void handleConfirmDeleteUpload()}
        onCancel={() => setPendingDeleteValues(null)}
      />
    </div>
  );
}
